import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentViewerV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerV2';
import { Flex, Loader, Tabs, Text } from '@mantine/core';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useDownloadedFileBlob } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlob';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { SignersTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab';
import type { SigningHeaderContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.types';
import { SignerSigningHeader } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerSigningHeader';
import { DetailsTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/DetailsTab';
import { ActivityTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/ActivityTab';
import { EmailScheduleTab } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/EmailScheduleTab';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { DocumentPreviewHeader } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/DocumentPreviewHeader';
import { useDeal } from '@dealstack/frontend/shared/modules/deal/hooks/useDeal';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { getFileId } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { useInvestmentProgrammeDocument } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useInvestmentProgrammeDocument';
import { useProgrammeDocumentsStream } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useProgrammeDocumentsStream';
import { usePendingDocumentAction } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/usePendingDocumentAction';
import { buildInvestmentProgrammeDocumentQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/documentQueryKeys';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';
import { LiveUpdatesPausedBanner } from '@dealstack/frontend/shared/components/LiveUpdatesPausedBanner/LiveUpdatesPausedBanner';
import { useLiveUpdatesPaused } from '@dealstack/frontend/shared/hooks/useLiveUpdatesPaused';
import { TabPanel } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/InvestmentProgrammeDocumentDetails.styled';
import { DocumentWorkflowInstanceSignatureStatus } from '@dealstack/shared/lib/generated';

export const InvestmentProgrammeDocumentDetails = () => {
	const { dealId, mipId, documentWorkflowInstanceId } = useRequiredParams(
		'dealId',
		'mipId',
		'documentWorkflowInstanceId'
	);
	const currentUserId = useAppSelector(selectUserId);
	const { data: deal } = useDeal({ dealId });
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const { value: automatedRemindersEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.AUTOMATED_EMAIL_REMINDERS
	);

	const {
		data: documentData,
		isLoading: isDocumentLoading,
		refetch: refetchDocument,
	} = useInvestmentProgrammeDocument({
		dealId,
		programmeId: mipId,
		documentWorkflowInstanceId,
	});

	const documentDetails = documentData?.payload;
	const isDocumentDetailsLoading = isDocumentLoading || !documentDetails;

	const { isConnected, isSupported, resync } = useProgrammeDocumentsStream({
		dealId,
		programmeId: mipId,
		documentWorkflowInstanceId,
	});
	const isLiveUpdatesPaused = useLiveUpdatesPaused({ isConnected, isSupported });

	const {
		isActionPending,
		isActionTimedOut,
		pendingSignerAssignmentAction,
		beginPendingAction,
		clearPendingAction,
	} = usePendingDocumentAction({ documentDetails, refetch: refetchDocument });

	const onDateApplied = async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: buildInvestmentProgrammeDocumentQueryKey({
					dealId,
					programmeId: mipId,
					documentWorkflowInstanceId,
				}),
			}),
			queryClient.invalidateQueries({
				queryKey: getDocumentWorkflowEventsQueryKey({
					programmeId: mipId,
					documentWorkflowInstanceId,
				}),
			}),
		]);
	};

	const fileId = documentDetails
		? getFileId({
				documentDetails,
				currentUserId: currentUserId ?? '',
			})
		: '';
	const documentName = documentDetails?.documentName ?? documentDetails?.fileName ?? '';
	const fileName = documentDetails?.fileName ?? '';

	const { data: fileData, isLoading: isFileLoading } = useDownloadedFileBlob({
		fileId,
		dealId,
	});

	const isViewerLoading =
		isDocumentDetailsLoading || isFileLoading || isActionPending || !fileData?.blob;

	// Claimed, generation pending: a signer is still in CREATED (the
	// generation-pending readiness signal for this feature) and the backend
	// hasn't produced a file yet, so there's nothing to preview. Bare `!fileId`
	// is too broad — fully-signed docs and non-WET_INK/non-ALL_PARTICIPANTS
	// docs can also legitimately have no fileId (see getFileId's branching in
	// Documents.utils.tsx) without being "in preparation".
	const hasSignerAwaitingGeneration =
		documentDetails?.groups.some((group) =>
			group.signers.some(
				(signer) => signer.status === DocumentWorkflowInstanceSignatureStatus.CREATED
			)
		) ?? false;
	const isAwaitingGeneration =
		!isDocumentDetailsLoading && !fileId && hasSignerAwaitingGeneration;

	const [signingHeader, setSigningHeader] = useState<SigningHeaderContext | null>(null);

	return (
		<Flex h="100%" direction="column" gap="1rem" p="1rem 2rem">
			<DocumentPreviewHeader deal={deal} mipId={mipId} documentName={documentName} />
			<LiveUpdatesPausedBanner
				isPaused={isLiveUpdatesPaused}
				isSupported={isSupported}
				isActionTimedOut={isActionTimedOut}
				onRefresh={resync}
			/>
			<Flex
				wrap="nowrap"
				style={{
					height: 'calc(90vh - 10vh)',
					border: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
					borderRadius: '0.25rem',
				}}
			>
				<div
					style={{
						flex: 1,
						minWidth: 0,
						minHeight: 0,
						padding: '0.5rem',
						position: 'relative',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
					}}
				>
					{isAwaitingGeneration ? (
						<Flex align="center" justify="center" direction="column" gap="sm" p="xl">
							<Loader size="lg" />
							<Text c="dimmed">{tMipManage('documents_page.preview.preparing')}</Text>
						</Flex>
					) : (
						<DocumentViewerV2
							file={fileData?.blob}
							fileName={fileName}
							fileExtension={fileData?.fileExtension}
							isLoading={isViewerLoading}
						/>
					)}
				</div>

				<Flex
					direction="column"
					w="28.25rem"
					h="100%"
					style={{
						flexShrink: 0,
						borderLeft: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
						overflow: 'hidden',
					}}
				>
					<Tabs
						defaultValue="signers"
						style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
					>
						{signingHeader ? (
							<SignerSigningHeader
								signerDisplayName={signingHeader.signerDisplayName}
								canGoBack={signingHeader.canGoBack}
								onBack={signingHeader.onBack}
							/>
						) : (
							<Tabs.List
								style={{
									borderBottom: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
									padding: '0.5rem 0.5rem 0 0.5rem',
								}}
							>
								<Tabs.Tab value="signers">Signers</Tabs.Tab>
								<Tabs.Tab value="details">Details</Tabs.Tab>
								<Tabs.Tab value="activity">Activity</Tabs.Tab>
								{automatedRemindersEnabled && (
									<Tabs.Tab value="email-schedule">Email schedule</Tabs.Tab>
								)}
							</Tabs.List>
						)}
						<TabPanel value="signers">
							<SignersTab
								dealId={dealId}
								programmeId={mipId}
								documentWorkflowInstanceId={documentWorkflowInstanceId}
								documentDetails={documentDetails}
								isLoading={isDocumentDetailsLoading}
								isActionPending={isActionPending}
								pendingSignerAssignmentAction={pendingSignerAssignmentAction}
								beginPendingAction={beginPendingAction}
								clearPendingAction={clearPendingAction}
								refetchDocument={refetchDocument}
								onSigningHeaderChange={setSigningHeader}
								onDateApplied={onDateApplied}
							/>
						</TabPanel>
						<TabPanel value="details">
							{documentDetails && (
								<DetailsTab
									dealId={dealId}
									programmeId={mipId}
									documentWorkflowInstanceId={documentWorkflowInstanceId}
									fileName={documentDetails.fileName}
									createdAt={new Date(documentDetails.createdAt)}
									updatedAt={new Date(documentDetails.updatedAt)}
									workflowStatus={documentDetails.workflowStatus}
									documentWorkflowTemplateType={
										documentDetails.documentWorkflowTemplateType
									}
									requiresPostDating={documentDetails.requiresPostDating}
									dateAppliedAt={documentDetails.dateAppliedAt}
									effectiveDate={documentDetails.effectiveDate}
									statusMessage={documentDetails.statusMessage}
									signerStatuses={documentDetails.groups.flatMap((group) =>
										group.signers.map((signer) => signer.status)
									)}
								/>
							)}
						</TabPanel>
						<TabPanel value="activity">
							<ActivityTab
								dealId={dealId}
								programmeId={mipId}
								documentWorkflowInstanceId={documentWorkflowInstanceId}
							/>
						</TabPanel>
						{automatedRemindersEnabled && (
							<TabPanel value="email-schedule">
								{documentDetails && (
									<EmailScheduleTab
										dealId={dealId}
										mipId={mipId}
										documentWorkflowInstanceId={documentWorkflowInstanceId}
										workflowStatus={documentDetails.workflowStatus}
									/>
								)}
							</TabPanel>
						)}
					</Tabs>
				</Flex>
			</Flex>
		</Flex>
	);
};
